import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const NamazTimings = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const cardsRef = useRef([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const autoPlayRef = useRef(null);

  const namazTimings = [
    {
      id: 1,
      prayer: 'Fajr',
      azanTime: '5:15 AM',
      namazTime: '5:30 AM',
      arabicName: 'صلاة الفجر',
      icon: '🌙✨',
      bgColor: 'bg-blue-100',
      timeOfDay: 'dawn'
    },
    {
      id: 2,
      prayer: 'Dhuhr',
      azanTime: '12:30 PM',
      namazTime: '12:45 PM',
      arabicName: 'صلاة الظهر',
      icon: '🌤️',
      bgColor: 'bg-amber-100',
      timeOfDay: 'noon'
    },
    {
      id: 3,
      prayer: 'Asr',
      azanTime: '4:00 PM',
      namazTime: '4:15 PM',
      arabicName: 'صلاة العصر',
      icon: '🌥️',
      bgColor: 'bg-orange-100',
      timeOfDay: 'afternoon'
    },
    {
      id: 4,
      prayer: 'Maghrib',
      azanTime: '6:15 PM',
      namazTime: '6:30 PM',
      arabicName: 'صلاة المغرب',
      icon: '🌙🌤️',
      bgColor: 'bg-rose-100',
      timeOfDay: 'sunset'
    },
    {
      id: 5,
      prayer: 'Isha',
      azanTime: '7:45 PM',
      namazTime: '8:00 PM',
      arabicName: 'صلاة العشاء',
      icon: '🌙',
      bgColor: 'bg-indigo-100',
      timeOfDay: 'night'
    },
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-play functionality - Fixed dependencies
  useEffect(() => {
    if (!isPaused) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % namazTimings.length);
      }, 2000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, namazTimings.length]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Stack cards showing left edges only
      cardsRef.current.forEach((card, index) => {
        if (!card) return;

        const depth = namazTimings.length - index;
        const leftOffset = isMobile ? index * 15 : index * 25;
        const yOffset = isMobile ? index * 5 : index * 10;
        const scale = isMobile ? 1 - (index * 0.015) : 1 - (index * 0.025);

        gsap.set(card, {
          x: isMobile ? -leftOffset : -leftOffset,
          y: yOffset,
          rotation: 0,
          scale: scale,
          zIndex: depth,
          transformOrigin: 'left center',
          opacity: 1,
        });

        // Hover effect only on desktop and only for current card
        if (!isMobile) {
          const handleMouseEnter = () => {
            if (index === currentIndex) {
              gsap.to(card, {
                x: -leftOffset + 5,
                scale: scale + 0.02,
                duration: 0.3,
                ease: 'power2.out'
              });
              setIsPaused(true);
            }
          };

          const handleMouseLeave = () => {
            if (index === currentIndex) {
              gsap.to(card, {
                x: -leftOffset,
                scale: scale,
                duration: 0.3,
                ease: 'power2.out'
              });
              setIsPaused(false);
            }
          };

          card.addEventListener('mouseenter', handleMouseEnter);
          card.addEventListener('mouseleave', handleMouseLeave);
        }
      });

      // Animate button on initial load
      if (!isLoaded) {
        const buttons = document.querySelectorAll('.arrow-button');
        gsap.fromTo(
          buttons,
          { scale: 0, opacity: 0 },
          { 
            scale: 1, 
            opacity: 1, 
            duration: 0.5, 
            delay: 0.3,
            ease: 'back.out(1.7)' 
          }
        );
        setIsLoaded(true);
      }

    }, sectionRef);

    return () => ctx.revert();
  }, [currentIndex, isLoaded, namazTimings.length, isMobile]);

  // Fixed animation logic
  useEffect(() => {
    const currentCard = cardsRef.current[currentIndex];
    const prevIndex = (currentIndex - 1 + namazTimings.length) % namazTimings.length;
    const prevCard = cardsRef.current[prevIndex];

    if (!currentCard) return;

    const leftOffset = isMobile ? 15 : 25;
    const yOffset = isMobile ? 5 : 10;
    const scaleStep = isMobile ? 0.015 : 0.025;

    // Animate the previous card flying away
    if (prevCard && currentIndex !== 0) {
      gsap.to(prevCard, {
        x: isMobile ? 800 : 1200,
        rotation: 10,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.inOut',
      });
    }

    // Bring current card to front
    gsap.to(currentCard, {
      x: 0,
      y: 0,
      scale: 1,
      zIndex: namazTimings.length,
      duration: 0.5,
      ease: 'power3.out',
      delay: 0.3,
    });

    // Reposition all other cards
    cardsRef.current.forEach((card, index) => {
      if (!card || index === currentIndex) return;

      let position;
      if (index > currentIndex) {
        position = index - currentIndex;
      } else {
        position = namazTimings.length - currentIndex + index;
      }

      const cardLeftOffset = position * leftOffset;
      const cardYOffset = position * yOffset;
      const scale = 1 - position * scaleStep;

      gsap.set(card, {
        x: -cardLeftOffset,
        y: cardYOffset,
        scale,
        rotation: 0,
        zIndex: namazTimings.length - position,
        opacity: 1,
      });
    });
  }, [currentIndex, isMobile, namazTimings.length]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % namazTimings.length);
    setIsPaused(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsPaused(false);
  };

  return (
    <section 
      ref={sectionRef}
      className="min-h-screen py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 xl:gap-20 items-center">

          {/* Title Section - Using Library Font Style */}
          <div 
            ref={titleRef} 
            className="order-1 lg:order-1 text-center lg:text-left mb-8 lg:mb-0"
          >
            <h1 className="text-6xl sm:text-7xl md:text-8xl xl:text-9xl font-black leading-none mb-4">
              AZAN &<br />NAMAZ<br />TIMINGS
            </h1>
            <p className="text-xl sm:text-2xl md:text-3xl font-light max-w-2xl opacity-90">
              Stay connected with your daily prayers through accurate timings
            </p>
          </div>

          {/* Cards Section */}
          <div className="order-2 lg:order-2 relative w-full h-[500px] sm:h-[600px] lg:h-[650px] flex items-center justify-end">
            <div className="relative w-full max-w-lg h-full">
              {namazTimings.map((namaz, index) => (
                <div
                  key={namaz.id}
                  ref={(el) => (cardsRef.current[index] = el)}
                  className={`absolute top-0 right-0 w-full h-full ${namaz.bgColor} rounded-[32px] sm:rounded-[40px] p-8 sm:p-10 md:p-12 lg:p-14 shadow-2xl`}
                  style={{ willChange: 'transform' }}
                >
                  <div className="flex flex-col h-full items-center justify-center text-center text-gray-800">
                    
                    {/* Icon */}
                    <div className="mb-6 sm:mb-8 lg:mb-10">
                      <span className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
                        {namaz.icon}
                      </span>
                    </div>

                    {/* Prayer Name - Using font-black like library */}
                    <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl  font-semibold text-gray-800 mb-2 sm:mb-3 tracking-tight leading-none">
                      {namaz.prayer.toUpperCase()}
                    </h3>

                    {/* Arabic Name - Using font-light */}
                    <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light text-gray-700 mb-8 sm:mb-10 lg:mb-12">
                      {namaz.arabicName}
                    </p>

                    {/* Timings Container */}
                    <div className="space-y-5 sm:space-y-6 lg:space-y-7">
                      {/* Azan Time */}
                      <div className="bg-white/40 backdrop-blur-sm rounded-2xl px-6 py-4 sm:px-8 sm:py-5 lg:px-10 lg:py-6 shadow-sm">
                        <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium mb-1 uppercase tracking-wider">
                          Azan Time
                        </p>
                        <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl  text-gray-800 tracking-tight">
                          {namaz.azanTime}
                        </p>
                      </div>

                      {/* Namaz Time */}
                      <div className="bg-white/40 backdrop-blur-sm rounded-2xl px-6 py-4 sm:px-8 sm:py-5 lg:px-10 lg:py-6 shadow-sm">
                        <p className="text-xs sm:text-sm md:text-base text-gray-600 font-medium mb-1 uppercase tracking-wider">
                          Namaz Time
                        </p>
                        <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-800 tracking-tight">
                          {namaz.namazTime}
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* Next Arrow Button */}
                  <button
                    onClick={handleNext}
                    disabled={index !== currentIndex}
                    className={`arrow-button absolute bottom-6 right-6 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full border-2 border-gray-800 bg-white/60 backdrop-blur-sm flex items-center justify-center transition-all duration-300 ${
                      index === currentIndex 
                        ? 'opacity-100 hover:bg-gray-800 hover:text-white hover:scale-110 cursor-pointer' 
                        : 'opacity-0 pointer-events-none'
                    }`}
                    aria-label="Next prayer time"
                  >
                    <svg 
                      className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 5l7 7-7 7" 
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {/* Progress Dots */}
            <div className="absolute -bottom-12 sm:-bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3">
              {namazTimings.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-gray-800 w-10 sm:w-12' 
                      : 'bg-gray-400 w-2 sm:w-2.5 hover:bg-gray-600'
                  }`}
                  aria-label={`Go to ${namazTimings[index].prayer} prayer`}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default NamazTimings;